import { Builder } from '@builder.io/react';
import { useRouter } from 'next/router';
import { Fragment, ReactNode } from 'react';
import { isNullOrUndefined } from '../../utils';
import {
  useAccounts,
  useCurrentAccount,
  useSession,
  useUserProfile,
} from '../../rest';

export enum DATASET_LABELS {
  allAccounts = 'All Accounts',
  currentAccount = 'Current Account',
  global = 'Global',
  queryParameters = 'Query Parameters',
  session = 'User',
  userProfile = 'User Profile',
}

export enum COMPARISONS {
  isTrue = 'is true',
  isFalse = 'is false',
  equals = 'equals',
  doesNotEqual = 'does not equal',
  contains = 'contains',
  doesNotContain = 'does not contain',
  startsWith = 'starts with',
  doesNotStartWith = 'does not start with',
  endsWith = 'ends with',
  doesNotEndWith = 'does not end with',
}

type Condition = {
  property: string;
  comparison: COMPARISONS;
  value: string;
  and?: Condition[];
};

type ConditionalProps = {
  children: ReactNode;
  conditions: Condition[];
  preview: boolean;
};

function someConditionUsesDataset(
  datasetLabel: DATASET_LABELS,
  conditions: Condition[],
): boolean {
  return conditions.some((condition) => {
    return (
      condition.property?.startsWith(`${datasetLabel} - `) ||
      (condition.and && someConditionUsesDataset(datasetLabel, condition.and))
    );
  });
}

/** Gets a value in a nested object using the provided array of keys */
function getValue(obj: Record<string, any>, keys: string[]): unknown {
  const [firstKey, ...remainingKeys] = keys;

  if (!Object.hasOwn(obj ?? {}, firstKey)) {
    return null;
  }

  const value = obj[firstKey];

  if (remainingKeys.length === 0) {
    return value;
  }

  return getValue(value, remainingKeys);
}

export function Conditional({
  conditions = [],
  preview,
  children,
}: ConditionalProps) {
  const router = useRouter();

  const shouldFetch = {
    allAccounts: someConditionUsesDataset(
      DATASET_LABELS.allAccounts,
      conditions,
    ),
    currentAccount: someConditionUsesDataset(
      DATASET_LABELS.currentAccount,
      conditions,
    ),
    session: someConditionUsesDataset(DATASET_LABELS.session, conditions),
    userProfile: someConditionUsesDataset(
      DATASET_LABELS.userProfile,
      conditions,
    ),
  };

  const allAccounts = useAccounts(shouldFetch.allAccounts);
  const currentAccount = useCurrentAccount(shouldFetch.currentAccount);
  const session = useSession(shouldFetch.session);
  const userProfile = useUserProfile(shouldFetch.userProfile);

  const data = {
    global: {
      is_loading:
        allAccounts.isLoading ||
        currentAccount.isLoading ||
        session.isLoading ||
        userProfile.isLoading,
    },
    allAccounts: {
      ...(allAccounts.data && {
        plan_info: {
          ids: allAccounts.data.map((account) => account.plan_info.id),
          names: allAccounts.data.map((account) => account.plan_info.name),
        },
      }),
      is_loading: allAccounts.isLoading,
    },
    currentAccount: {
      ...currentAccount.data,
      is_loading: currentAccount.isLoading,
    },
    session: { ...session.data, is_loading: session.isLoading },
    userProfile: { ...userProfile.data, is_loading: userProfile.isLoading },
    queryParameters: router.query,
  };

  const conditionIsSatisfied = (condition: Condition) => {
    if (!condition.property || !condition.comparison) {
      return false;
    }

    const [datasetLabel, ...keys] = condition.property.split(' - ');

    const datasetKey = {
      [DATASET_LABELS.allAccounts]: 'allAccounts',
      [DATASET_LABELS.currentAccount]: 'currentAccount',
      [DATASET_LABELS.session]: 'session',
      [DATASET_LABELS.userProfile]: 'userProfile',
      [DATASET_LABELS.queryParameters]: 'queryParameters',
      [DATASET_LABELS.global]: 'global',
    }[datasetLabel];

    if (!datasetKey) {
      return false;
    }

    const actualValue = getValue(data, [datasetKey, ...keys]);
    const expectedValue = `${condition.value}`;

    switch (condition.comparison) {
      case COMPARISONS.isTrue:
        return !!actualValue;

      case COMPARISONS.isFalse:
        return !actualValue;

      case COMPARISONS.equals:
        return (
          !isNullOrUndefined(actualValue) && `${actualValue}` === expectedValue
        );

      case COMPARISONS.doesNotEqual:
        return (
          isNullOrUndefined(actualValue) || `${actualValue}` !== expectedValue
        );

      case COMPARISONS.contains:
        return !isNullOrUndefined(actualValue) && Array.isArray(actualValue)
          ? actualValue.includes(expectedValue)
          : `${actualValue}`.includes(expectedValue);

      case COMPARISONS.doesNotContain:
        return (
          isNullOrUndefined(actualValue) ||
          !(Array.isArray(actualValue)
            ? actualValue.includes(expectedValue)
            : `${actualValue}`.includes(expectedValue))
        );

      case COMPARISONS.startsWith:
        return (
          !isNullOrUndefined(actualValue) &&
          `${actualValue}`.startsWith(expectedValue)
        );

      case COMPARISONS.doesNotStartWith:
        return (
          isNullOrUndefined(actualValue) ||
          !`${actualValue}`.startsWith(expectedValue)
        );

      case COMPARISONS.endsWith:
        return (
          !isNullOrUndefined(actualValue) &&
          `${actualValue}`.endsWith(expectedValue)
        );

      case COMPARISONS.doesNotEndWith:
        return (
          isNullOrUndefined(actualValue) ||
          !`${actualValue}`.endsWith(expectedValue)
        );

      default:
        return false;
    }
  };

  const someConditionIsSatisfied = conditions?.some((condition) => {
    return (
      conditionIsSatisfied(condition) &&
      (condition.and || []).every((andCondition) => {
        return conditionIsSatisfied(andCondition);
      })
    );
  });

  /**
   * Render nothing if there are no children, the page is being
   * previewed/edited and `preview` is disabled, or the page is not being
   * previewed/edited and no conditions are satisfied
   * */
  if (
    !children ||
    (Builder.isEditing || Builder.isPreviewing
      ? !preview
      : !someConditionIsSatisfied)
  ) {
    return null;
  }

  return <Fragment>{children}</Fragment>;
}
